import { buildLineGuideShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';
import {
    defineConnectorConstellationDerivation,
    defineConnectorItemDerivation,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toConnectorItemCollectionDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { EntityItemParametricStateByKey } from '@toolproof-core/visualization/_contract/item';
import type { ConnectorItemDerivation, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { FLAG_ABOVE_ITEM_LABEL } from '@/workflow/_data/config/fragments/labels';
import { productGeometry } from '@/workflow-runner/conceptProduct';
import { compositeFlagLabels } from '@/workflow-runner/conceptCompositeFlag';
import type { Config } from '@/workflow/_contract/config';

export const UNIVERSE_ITEM_KEY = 'item:entity:ResourceType:HigherUniverse';
const UNIVERSE_COLLECTION_KEY = 'collection:entity:ResourceType:HigherUniverse';
const SPOKES_COLLECTION_KEY = 'collection:connector:ResourceType:HigherUniverse';
const GROUPS = [
    { id: 'Product', handles: ['TYPE-Natural', 'TYPE-Boolean'], text: 'Natural × Boolean', angle: Math.PI * 7 / 6 },
    { id: 'Sum', handles: ['TYPE-MockAlpha', 'TYPE-MockGamma'], text: 'α + γ', angle: Math.PI * 11 / 6 },
] as const;
const SINGLES = [
    { handle: 'TYPE-MockBeta', angle: Math.PI * 2 },
    { handle: 'TYPE-MockDelta', angle: Math.PI * 8 / 3 },
] as const;

function polar(radius: number, angle: number): Vector3Tuple {
    return [radius * Math.cos(angle), 0, radius * Math.sin(angle)];
}
function subtract(a: Vector3Tuple, b: Vector3Tuple): Vector3Tuple {
    return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function withUniverseConfig(config: Config): Config {
    const { halfAngle } = productGeometry(config);
    const angles = Object.fromEntries([
        ...GROUPS.flatMap(group => group.handles.map((handle, index) =>
            [handle, group.angle + (index === 0 ? -halfAngle : halfAngle)])),
        ...SINGLES.map(type => [type.handle, type.angle]),
    ]);
    return { ...config, domainPresentation: { ...config.domainPresentation,
        entities: { ...config.domainPresentation.entities,
            resourceTypes: { ...config.domainPresentation.entities.resourceTypes,
                angularPlacement: { placementKind: 'explicit', angleByResourceTypeHandle: angles } } } } };
}

export function deriveUniverseCollections(config: Config) {
    const { ringRadius, halfAngle } = productGeometry(config);
    const radius = ringRadius * Math.sin(halfAngle);
    const typeItem = config.domainPresentation.entities.resourceTypes.item;
    const flag = typeItem?.label?.aboveItem ?? FLAG_ABOVE_ITEM_LABEL;
    const spokes: ConnectorItemDerivation[] = [];
    const add = (id: string, handle: string, from: Vector3Tuple, to: Vector3Tuple) => {
        spokes.push(defineConnectorItemDerivation({
            item: { itemKind: 'connector', itemKey: `item:connector:ResourceType:HigherUniverse:${id}`,
                shape: buildLineGuideShape(),
                endpoints: {
                    from: { endpointKind: 'static', itemKey: UNIVERSE_ITEM_KEY, anchorKey: 'center' },
                    to: { endpointKind: 'static', itemKey: `item:entity:${handle}`, anchorKey: 'center' },
                } },
            parametricState: { thickness: radius * 0.035,
                visualStyle: { color: typeItem?.visualStyle?.color, opacity: 1 },
                endpointPositions: { from, to } },
        }));
    };
    for (const type of SINGLES) {
        add(type.handle, type.handle, polar(radius, type.angle), polar(ringRadius - radius, type.angle));
    }
    for (const group of GROUPS) {
        const fork = polar(radius + (ringRadius - radius) * 0.8, group.angle);
        add(`${group.id}:trunk`, group.handles[0], polar(radius, group.angle), fork);
        group.handles.forEach((handle, index) => {
            const center = polar(ringRadius, group.angle + (index === 0 ? -halfAngle : halfAngle));
            const delta = subtract(fork, center);
            const distance = Math.hypot(...delta);
            const surface: Vector3Tuple = [
                center[0] + delta[0] * radius / distance,
                center[1] + delta[1] * radius / distance,
                center[2] + delta[2] * radius / distance,
            ];
            add(`${group.id}:prong:${index}`, handle, fork, surface);
        });
    }
    return [
        toEntityItemCollectionDerivation({
            itemCollectionKey: UNIVERSE_COLLECTION_KEY,
            itemDerivations: [defineEntityItemDerivation({
                item: { itemKind: 'entity', itemKey: UNIVERSE_ITEM_KEY, name: 'ResourceType',
                    description: 'A higher-universe ResourceType whose instances are the displayed lower-universe types' },
                parametricState: { shape: buildSphereShape({ radius }), position: [0, 0, 0],
                    visualStyle: typeItem?.visualStyle,
                    label: { aboveItem: { ...flag, text: 'ResourceType' } } },
            })],
        }),
        toConnectorItemCollectionDerivation({ itemCollectionKey: SPOKES_COLLECTION_KEY, itemDerivations: spokes }),
    ];
}

export function deriveUniverseConstellations() {
    return [
        defineEntityConstellationDerivation({
            constellation: { constellationKind: 'entity', constellationKey: 'constellation:entity:ResourceType:HigherUniverse',
                itemCollectionKey: UNIVERSE_COLLECTION_KEY, anchor: { itemKey: UNIVERSE_ITEM_KEY, anchorKey: 'center' } },
            parametricState: { layout: { layoutKind: 'spoke', anchor: { anchorKind: 'origin' }, originNearestPosition: [0, 0, 0],
                direction: [1, 0, 0], spacing: 1 } },
        }),
        defineConnectorConstellationDerivation({
            constellation: { constellationKind: 'connector', constellationKey: 'constellation:connector:ResourceType:HigherUniverse',
                itemCollectionKey: SPOKES_COLLECTION_KEY },
        }),
    ];
}

export function universeCompositeLabels(config: Config) {
    const { ringRadius, halfAngle } = productGeometry(config);
    const sphereRadius = ringRadius * Math.sin(halfAngle);
    return GROUPS.flatMap(group => compositeFlagLabels({
        config, sphereRadius, text: group.text,
        components: group.handles.map((handle, index) => ({
            itemKey: `item:entity:${handle}`,
            center: polar(ringRadius, group.angle + (index === 0 ? -halfAngle : halfAngle)),
        })),
    }));
}

export function deriveUniverseTransitions(config: Config, revision: number): TransitionPhase[] {
    const { ringRadius, halfAngle } = productGeometry(config);
    const labels = universeCompositeLabels(config);
    const sum = GROUPS[1];
    const spokes = deriveUniverseCollections(config).flatMap(collection =>
        collection.itemDerivations.filter(derivation => derivation.item.itemKind === 'connector'));
    const phases: TransitionPhase[] = Array.from({ length: 49 }, (_, index) => {
        const t = index / 48;
        const progress = t * t * (3 - 2 * t);
        const positions = sum.handles.map((handle, component) => {
            const from = polar(ringRadius, component === 0 ? Math.PI * 5 / 3 : Math.PI * 7 / 3);
            const to = polar(ringRadius, sum.angle + (component === 0 ? -halfAngle : halfAngle));
            const position: Vector3Tuple = [
                from[0] + (to[0] - from[0]) * progress, 0, from[2] + (to[2] - from[2]) * progress,
            ];
            return [`item:entity:${handle}`, { position }] as const;
        });
        const positionByKey: EntityItemParametricStateByKey = Object.fromEntries(positions);
        return {
            transitionOptions: { transitionKind: 'instant', delay: index === 0 ? 0 : 25,
                transitionKey: `concept-universe:${revision}:${index}` },
            patch: {
                entityItemLabelChannelsToRemoveByKey: Object.fromEntries(
                    (index === 48 ? labels.filter(entry => !entry.state.label?.aboveItem) : labels)
                        .map(entry => [entry.itemKey, ['aboveItem']])),
                itemParametricState: {
                    entityItemByKey: {
                        [UNIVERSE_ITEM_KEY]: { visualStyle: { opacity: 0 } },
                        ...positionByKey,
                        ...(index === 48 ? Object.fromEntries(labels.map(entry =>
                            [entry.itemKey, { ...entry.state, ...positionByKey[entry.itemKey] }])) : {}),
                    },
                    connectorItemByKey: Object.fromEntries(spokes.map(entry =>
                        [entry.item.itemKey, { visualStyle: { opacity: 0 } }])),
                },
            },
        };
    });
    phases.push({
        transitionOptions: { transitionKind: 'interpolated', duration: 400, easing: 'smoothstep',
            transitionKey: `concept-universe:${revision}:spokes` },
        patch: { itemParametricState: {
            entityItemByKey: { [UNIVERSE_ITEM_KEY]: { visualStyle: { opacity: 1 } } },
            connectorItemByKey: Object.fromEntries(spokes.map(entry => [entry.item.itemKey, { visualStyle: { opacity: 1 } }])),
        } },
    });
    return phases;
}
